import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Plus, Trash2, Search, Pencil, Upload } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { layoutForPath } from '../utils/layout';
import CustomerFormDialog from '../components/customer/CustomerFormDialog';
import DataImportDialog from '../components/import/DataImportDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Customers() {
  const [customers, setCustomers] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, customer: null });
  const [importOpen, setImportOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    }
  };

  const openAddDialog = () => {
    setEditingCustomer(null);
    setShowDialog(true);
  };

  const openEditDialog = (customer) => {
    setEditingCustomer(customer);
    setShowDialog(true);
  };

  const handleDeleteCustomer = async () => {
    if (!deleteConfirm.customer) return;
    try {
      await axios.delete(`${API}/customers/${deleteConfirm.customer.id}`);
      toast.success('Customer deleted');
      setDeleteConfirm({ open: false, customer: null });
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  // Filter and sort customers alphabetically by name
  const filtered = customers
    .filter(c => {
      const s = searchTerm.toLowerCase();
      return (c.name.toLowerCase().includes(s) ||
              (c.sites || []).some(site => site.name.toLowerCase().includes(s) || site.lsd.toLowerCase().includes(s)));
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const canManage = ['management', 'field_coordinator', 'field_management'].includes(user?.role)
    || (user?.effective_permissions || []).includes('customers.edit');
  const location = useLocation();
  const Layout = layoutForPath(location.pathname);

  return (
    <Layout title="Customers">
      <div className="space-y-6" data-testid="customers-page">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Customers</h3>
            <p className="text-slate-600">Manage customer database</p>
          </div>
          {canManage && (
            <div className="flex gap-2">
              {user?.role === 'management' && (
                <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-customers-button">
                  <Upload size={16} className="mr-2" /> Import CSV
                </Button>
              )}
              <Button onClick={openAddDialog} className="bg-slate-900 hover:bg-slate-800" data-testid="add-customer-button">
                <Plus size={20} className="mr-2" />
                Add Customer
              </Button>
            </div>
          )}
        </div>

        <CustomerFormDialog open={showDialog} onOpenChange={setShowDialog} customer={editingCustomer} onSaved={fetchCustomers} />
        <DataImportDialog kind="customers" open={importOpen} onOpenChange={setImportOpen} onImported={fetchCustomers} />

        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
          <Input 
            placeholder="Search..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            className="pl-10" 
            data-testid="search-customers-input"
          />
        </div>

        <div className="bg-white border border-slate-200 rounded-md shadow-sm">
          <table className="w-full" data-testid="customers-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase text-slate-500">Name</th>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase text-slate-500">Sites (Name — LSD / Address)</th>
                {canManage && <th className="px-6 py-3 text-left text-xs font-bold uppercase text-slate-500">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filtered.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
                  <td className="px-6 py-4 text-sm font-medium">
                    <Link to={`${location.pathname}/${c.id}`} className="text-slate-900 hover:text-sky-700 hover:underline" data-testid={`customer-link-${c.id}`}>
                      {c.name}
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600" data-testid={`customer-sites-${c.id}`}>
                    {(c.sites || []).length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {c.sites.map((s) => (
                          <span key={s.id} className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-xs">
                            {s.name || <span className="italic text-slate-400">no name</span>}
                            {s.lsd && <span className="ml-1.5 font-mono text-slate-500">{s.lsd}</span>}
                          </span>
                        ))}
                      </div>
                    ) : 'N/A'}
                  </td>
                  {canManage && (
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => openEditDialog(c)}
                          data-testid={`edit-customer-${c.id}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => setDeleteConfirm({ open: true, customer: c })} 
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`delete-customer-${c.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="p-12 text-center text-slate-600">
              {searchTerm ? 'No matches' : 'No customers'}
            </div>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => setDeleteConfirm({ open, customer: open ? deleteConfirm.customer : null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Customer</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{deleteConfirm.customer?.name}</strong>? 
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteCustomer} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-customer-btn">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}

export default Customers;
